import { Alert, Button, Form, Input } from "antd";
import axios from "axios";
import { LockIcon, MailIcon } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { PortalAuthCard } from "../components/PortalProduct";
import { useTenant } from "../tenant/TenantContext";

type SignupValues = { agencyName: string; contactName: string; email: string; phone?: string; password: string };

/** Lets a prospective agency request a portal account; the organisation must approve it before sign-in works. */
const PortalSignupPage = () => {
  const tenant = useTenant();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!tenant.tenantId || !tenant.selfSignupEnabled) {
    return (
      <PortalAuthCard eyebrow="Partner portal" title="Account requests are closed" description="This organisation creates portal accounts for its partners directly.">
        <Link className="mt-5 inline-block text-sm text-(--brand-primary)" to="/login">
          Back to sign in
        </Link>
      </PortalAuthCard>
    );
  }

  return (
    <PortalAuthCard
      eyebrow="Partner portal"
      title="Request an account"
      description={`Tell ${tenant.tenantName || "the organisation"} about your agency. You can sign in once your request is approved.`}
    >
      {submitted ? (
        <>
          <Alert className="!mt-6" type="success" showIcon message="Request sent" description="We will email you when it has been reviewed." />
          <Link className="mt-5 inline-block text-sm text-(--brand-primary)" to="/login">
            Back to sign in
          </Link>
        </>
      ) : (
        <Form<SignupValues>
          className="mt-6"
          layout="vertical"
          requiredMark="optional"
          onFinish={async ({ confirmPassword: _confirmPassword, ...values }: SignupValues & { confirmPassword?: string }) => {
            try {
              setSubmitting(true);
              setError(null);
              await axios.post("/api/agent-portal/auth/signup", values);
              setSubmitted(true);
            } catch (requestError: unknown) {
              setError(
                axios.isAxiosError<{ error?: string }>(requestError)
                  ? requestError.response?.data?.error || "Unable to send your request"
                  : "Unable to send your request",
              );
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {error ? <Alert className="mb-5" type="error" showIcon message={error} /> : null}
          <Form.Item label="Agency name" name="agencyName" rules={[{ required: true, min: 2, max: 128, message: "Enter your agency name" }]}>
            <Input autoComplete="organization" maxLength={128} />
          </Form.Item>
          <Form.Item label="Your full name" name="contactName" rules={[{ required: true, min: 2, max: 128, message: "Enter your full name" }]}>
            <Input autoComplete="name" maxLength={128} />
          </Form.Item>
          <Form.Item label="Work email" name="email" rules={[{ required: true, type: "email", message: "Enter a valid email" }]}>
            <Input prefix={<MailIcon size={15} className="text-gray-400" />} autoComplete="email" maxLength={128} />
          </Form.Item>
          <Form.Item label="Phone" name="phone" rules={[{ pattern: /^[+\d][\d\s()\-.]{5,31}$/, message: "Enter a valid phone number" }]}>
            <Input autoComplete="tel" maxLength={32} />
          </Form.Item>
          <Form.Item label="Choose a password" name="password" rules={[{ required: true, min: 8, max: 128, message: "Use 8 to 128 characters" }]}>
            <Input.Password prefix={<LockIcon size={15} className="text-gray-400" />} autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            label="Confirm password"
            name="confirmPassword"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Confirm your password" },
              ({ getFieldValue }) => ({
                validator: async (_, value) => {
                  if (!value || getFieldValue("password") === value) return;
                  throw new Error("Passwords do not match");
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={submitting}>
            Send request
          </Button>
          <Link className="mt-5 inline-block text-sm text-(--brand-primary)" to="/login">
            Back to sign in
          </Link>
        </Form>
      )}
    </PortalAuthCard>
  );
};

export default PortalSignupPage;
